/* eslint-disable import/no-cycle */
/* eslint-disable react/require-default-props */
import React, { useRef } from 'react';
import { Box, Button, HStack } from '@chakra-ui/core';
import { MdDragIndicator } from 'react-icons/md';
import { HiTrash } from 'react-icons/hi';
import { FORM_COMPONENT_SCHEMAS } from './schemas/formFieldSchema';
import DeleteFormElement from './actions/DeleteFormElement';
import { InputKeyField } from './InputKeyField';
import useFieldDragAndDrop from './hooks/useFieldDragAndDrop';
import { removeFollowupOptionPath } from './utils';
import { useStoreActions } from '../../../models/hooks';
import { DropTarget, FormFieldT } from './types';

interface FormFieldProps {
  field: FormFieldT;
  path: number[];
  onDropItem: (dropTarget: DropTarget) => void;
  parentIsAdvanced?: boolean;
  optionId?: string;
  followupPath?: number[];
}

export const FormFieldComponent: React.FC<FormFieldProps> = React.memo(
  ({ field, path, onDropItem, parentIsAdvanced, optionId, followupPath }) => {
    const { removeItemByPath, removeError } = useStoreActions((actions) => actions.formBuilder);

    const itemRef = useRef<HTMLDivElement>(null);

    const { component: Component } =
      React.useMemo(
        () => FORM_COMPONENT_SCHEMAS?.[field.inputType],
        /* eslint-disable-line react-hooks/exhaustive-deps */ [field.inputType],
      ) || {};
    const { handleDragStart, handleDragEnd, handleItemDragOver, handleItemDrop } = useFieldDragAndDrop({
      path,
      field,
      itemRef,
      onDropItem,
      parentIsAdvanced,
    });
    const actionClassName = `field-actions-${field.id}`;
    const hoverAction = `&:hover .${actionClassName}`;
    return (
      <Box
        ref={itemRef}
        onDragOver={handleItemDragOver}
        onDrop={handleItemDrop}
        sx={{
          pl: 4,
          pr: 2,
          position: 'relative',
          [hoverAction]: { display: 'flex' },
          borderRadius: 'md',
        }}
        id={field.id}
        data-id={field.id}
        data-path={path.join(',')}
      >
        <HStack
          sx={{
            borderRadius: 'md',
            backgroundColor: optionId ? 'transparent' : '#fff',
          }}
          align="flex-start"
        >
          <Box flexGrow={1}>
            {Component && (
              <Component
                fieldId={field.id}
                isAdvanced={parentIsAdvanced}
                path={path}
                labelRightRenderer={
                  <Box>
                    <InputKeyField isAdvanced={parentIsAdvanced} fieldId={field.id} />
                  </Box>
                }
              />
            )}
          </Box>
          <HStack flex="0 0 80px" className="hide-in-drag-preview" justify="flex-end" align="flex-start" spacing={0}>
            {!parentIsAdvanced && (
              <HStack className={actionClassName} justify="flex-end" display="none">
                <Button
                  as="span"
                  cursor="grab"
                  draggable={!parentIsAdvanced}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  data-testid={`drag-handle-field-${field.id}`}
                  borderRadius="50%"
                  backgroundColor="gray"
                  style={{ aspectRatio: '1/1' }}
                  padding={1}
                  size="sm"
                >
                  <MdDragIndicator />
                </Button>
                <DeleteFormElement
                  popoverTrigger={
                    <Button borderRadius="50%" colorScheme="red" style={{ aspectRatio: '1/1' }} padding={1} size="sm">
                      <HiTrash />
                    </Button>
                  }
                  onYes={() => {
                    let originalPath = [...path];
                    if (followupPath) {
                      originalPath = removeFollowupOptionPath(followupPath, path);
                    }
                    removeItemByPath({ path: originalPath });
                    removeError({ targetId: field.id });
                  }}
                />
              </HStack>
            )}
          </HStack>
        </HStack>
      </Box>
    );
  },
);
export const FormField = FormFieldComponent;
